import React, { useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useMountEffect } from '../../hooks/useMountEffect'
import { ArrowUpRight, Play, X } from 'lucide-react'
import { cn } from '../UI/cn'
import { Tooltip } from '../Tooltip/tooltip'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../UI/Modal/ModalPrimitive'
import type {
  EnrichedAction,
  PreparedAction,
  ActionClickStatus,
  ActionClickUpdateStatus,
} from '../../services/recommendations_contracts'
import {
  URGENCY_CIRCLE_STYLES,
  URGENCY_ICONS,
  URGENCY_TOOLTIPS,
  SIGNAL_TOOLTIPS,
} from './constants'

interface EnrichedActionCardProps {
  action: EnrichedAction
  /** Optional callback when the card is clicked (for tracking) */
  onCardClick?: () => void
  /** Optional callback when user clicks Dismiss */
  onStatusClick?: (status: ActionClickUpdateStatus) => void
  /** Current action status (for visual treatment) */
  actionStatus?: ActionClickStatus | null
  /** Whether the action was auto-completed by the system */
  actionAutoCompleted?: boolean
  /** Optional callback when user confirms the prepared action (stage change) */
  onApplyPreparedAction?: (preparedAction: PreparedAction) => void
}

/**
 * Apply button for prepared actions. Shows a confirmation modal before
 * executing. Title and body text come from the backend-provided
 * preparedAction.confirmation_title / confirmation_body fields so the
 * frontend stays agnostic to action type.
 */
interface ApplyActionButtonProps {
  preparedAction: PreparedAction
  disabled: boolean
  disabledReason?: 'stale' | 'finalized'
  onApply: (preparedAction: PreparedAction) => void
}

const DISABLED_TOOLTIP: Record<
  'stale' | 'finalized',
  { title: string; content: string }
> = {
  stale: {
    title: 'Outdated',
    content:
      'The deal stage has already changed — this action is no longer applicable.',
  },
  finalized: {
    title: 'Already actioned',
    content:
      'This recommendation has already been marked as done or dismissed.',
  },
}

const ApplyActionButton: React.FC<ApplyActionButtonProps> = ({
  preparedAction,
  disabled,
  disabledReason,
  onApply,
}) => {
  const [open, setOpen] = useState(false)

  const handleConfirm = (e: React.MouseEvent) => {
    e.stopPropagation()
    setOpen(false)
    onApply(preparedAction)
  }

  const handleCancel = (e: React.MouseEvent) => {
    e.stopPropagation()
    setOpen(false)
  }

  if (disabled) {
    const tooltip = DISABLED_TOOLTIP[disabledReason ?? 'stale']
    return (
      <Tooltip
        title={tooltip.title}
        content={tooltip.content}
        placement="top"
        delay={[200, 0]}
      >
        <button
          type="button"
          disabled
          className="inline-flex items-center gap-1 px-2 py-1 text-xs rounded opacity-40 cursor-not-allowed bg-fb-dark-green text-white"
        >
          <Play size={12} fill="currentColor" />
          <span>Apply</span>
        </button>
      </Tooltip>
    )
  }

  return (
    <>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation()
          setOpen(true)
        }}
        className="inline-flex items-center gap-1 px-2 py-1 text-xs rounded transition-colors bg-fb-dark-green text-white hover:bg-fb-dark-green-hover font-medium"
      >
        <Play size={12} fill="currentColor" />
        <span>Apply</span>
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent size="md">
          <DialogHeader>
            <DialogTitle>
              {preparedAction.confirmation_title ?? 'Apply action?'}
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-fg-secondary">
            {preparedAction.confirmation_body ??
              'Are you sure you want to apply this action?'}
          </p>
          <DialogFooter>
            <button
              type="button"
              onClick={handleCancel}
              className="px-4 py-2 text-sm rounded border border-line text-fg-secondary hover:bg-surface-alt transition-colors"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleConfirm}
              className="px-4 py-2 text-sm rounded bg-fb-dark-green text-white hover:bg-fb-dark-green-hover transition-colors font-medium"
            >
              Confirm
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}

const GREEN_BADGE = 'bg-fb-green-50 text-fb-dark-green border-fb-green-100'
const GREY_BADGE = 'bg-surface-alt text-fg-secondary border-line-subtle'

function getResolvedBadge(
  status: ActionClickStatus | null,
  autoCompleted: boolean
): { label: string; className: string } | null {
  if (status === 'done') {
    return {
      label: autoCompleted ? 'Manually Resolved' : 'Applied',
      className: GREEN_BADGE,
    }
  }
  if (status === 'dismissed') {
    return {
      label: autoCompleted ? 'Manually Resolved' : 'Dismissed',
      className: autoCompleted ? GREEN_BADGE : GREY_BADGE,
    }
  }
  return null
}

/**
 * Compact action card with risk indicator and company link.
 *
 * Features:
 * - Colored icon circle driven by urgency (red=do_now, amber=today, green=this_week)
 * - Black border, no left stripe
 * - Dismiss (7-day suppression) and Apply (stage change) actions in footer
 * - Link to deal (email_stage_change) or company (all others)
 * - Context snippets from last interaction
 */
export const EnrichedActionCard: React.FC<EnrichedActionCardProps> = ({
  action,
  onCardClick,
  onStatusClick,
  actionStatus = null,
  actionAutoCompleted = false,
  onApplyPreparedAction,
}) => {
  const navigate = useNavigate()
  const [isExpanded, setIsExpanded] = useState(false)
  const [isTruncated, setIsTruncated] = useState(false)
  const textRef = useRef<HTMLParagraphElement>(null)
  const { iconBg, iconColor } = URGENCY_CIRCLE_STYLES[action.urgency]
  const UrgencyIcon = URGENCY_ICONS[action.urgency]
  const signalTypeLabel = SIGNAL_TOOLTIPS[action.signal_type].title

  const isStatusFinalized =
    actionStatus === 'done' || actionStatus === 'dismissed'
  const resolvedBadge = getResolvedBadge(actionStatus, actionAutoCompleted)

  // email_stage_change actions link to the deal; all others link to the company
  const destination =
    action.signal_type === 'email_stage_change' && action.deal_id
      ? `/deals/${action.deal_id}/`
      : action.company_link
  const viewLabel =
    action.signal_type === 'email_stage_change' ? 'View deal' : 'View company'

  const handleCardClick = () => {
    setIsExpanded((prev) => !prev)
  }

  const handleNavigate = (e: React.MouseEvent) => {
    e.stopPropagation()
    if (onCardClick) onCardClick()
    navigate(destination)
  }

  const handleExpandClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    setIsExpanded((prev) => !prev)
  }

  // Detect if text is actually truncated by line-clamp (measured once on mount
  // while collapsed; text content is stable for the lifetime of a given card).
  useMountEffect(() => {
    const el = textRef.current
    if (el) {
      setIsTruncated(el.scrollHeight > el.clientHeight)
    }
  })

  return (
    <div
      onClick={handleCardClick}
      className={cn(
        'rounded-lg border border-fg bg-surface p-3 cursor-default',
        actionStatus === 'done' && 'opacity-50',
        actionStatus === 'dismissed' && 'opacity-40'
      )}
    >
      <div className="flex items-start gap-2">
        {/* Risk Icon with Tooltip */}
        <Tooltip
          title={URGENCY_TOOLTIPS[action.urgency].title}
          content={URGENCY_TOOLTIPS[action.urgency].content}
          placement="top"
          delay={[500, 0]}
        >
          <div
            className={cn(
              'flex-shrink-0 flex items-center justify-center w-8 h-8 rounded-full',
              iconBg,
              iconColor
            )}
          >
            <UrgencyIcon size={16} />
          </div>
        </Tooltip>

        <div className="flex-1 min-w-0">
          {/* Header: Signal type label + company + action task */}
          <div className="flex items-start justify-between gap-2">
            <div className="flex-1 min-w-0">
              <h4 className="font-semibold text-fg text-sm leading-tight">
                {signalTypeLabel}
              </h4>
              <div className="flex items-center gap-1.5 mt-0.5 text-xs text-fg-secondary">
                <span className="text-fb-dark-green font-medium">
                  {action.company_name}
                </span>
                {action.company_subcategory && (
                  <>
                    <span className="text-line">·</span>
                    <span className="text-fg-muted">
                      {action.company_subcategory}
                    </span>
                  </>
                )}
                {action.contact_name && (
                  <>
                    <span className="text-line">|</span>
                    <span>{action.contact_name}</span>
                  </>
                )}
              </div>
            </div>
            {resolvedBadge && (
              <span
                className={cn(
                  'ml-2 flex-shrink-0 inline-flex items-center rounded-full border px-2 py-0.5 text-xxs font-medium',
                  resolvedBadge.className
                )}
              >
                {resolvedBadge.label}
              </span>
            )}
          </div>

          {/* Action task — specific what-to-do */}
          <p className="text-sm text-fg mt-1">{action.action}</p>

          {/* Recommendation - expandable */}
          <p
            ref={textRef}
            onClick={handleExpandClick}
            className={cn(
              'text-sm text-fg-secondary mt-1',
              !isExpanded && 'line-clamp-4'
            )}
          >
            {action.recommendation}
          </p>
          {!isExpanded && isTruncated && (
            <button
              type="button"
              onClick={handleExpandClick}
              className="text-xs text-fb-dark-green hover:text-fb-dark-green-hover font-medium mt-0.5"
            >
              Show more
            </button>
          )}

          {/* Context Snippet - only if exists and compact */}
          {action.context_snippet && (
            <p className="text-xs text-fg-muted italic mt-1 line-clamp-1">
              &ldquo;{action.context_snippet}&rdquo;
            </p>
          )}

          {/* Action footer: View link (left) + Apply + Dismiss (right) */}
          <div className="flex items-center justify-between mt-2 pt-2 border-t border-line-subtle">
            <button
              type="button"
              onClick={handleNavigate}
              className="inline-flex items-center gap-1 text-xs text-fb-dark-green hover:text-fb-dark-green-hover font-medium transition-colors"
            >
              {viewLabel}
              <ArrowUpRight size={12} />
            </button>
            <div className="flex items-center gap-1">
              {onApplyPreparedAction &&
                (action.prepared_actions ?? []).map((pa) => (
                  <ApplyActionButton
                    key={pa.target_stage_id}
                    preparedAction={pa}
                    disabled={isStatusFinalized || !!pa.stale}
                    disabledReason={
                      isStatusFinalized
                        ? 'finalized'
                        : pa.stale
                          ? 'stale'
                          : undefined
                    }
                    onApply={onApplyPreparedAction}
                  />
                ))}
              {!isStatusFinalized && (
                <Tooltip
                  title="Dismiss"
                  content="Hide this recommendation for 7 days."
                  placement="top"
                  delay={[300, 0]}
                >
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation()
                      onStatusClick?.('dismissed')
                    }}
                    className="inline-flex items-center gap-1 px-2 py-1 text-xs rounded transition-colors text-fg-secondary hover:text-fg hover:bg-surface-alt"
                    aria-label="Dismiss"
                  >
                    <X size={14} />
                    <span>Dismiss</span>
                  </button>
                </Tooltip>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
